@if (!isLoading) {
  <div class="main-container" data-cy="forms-builder-item-container">
    <!-- Header -->
    <div class="section-header" data-cy="forms-builder-item-header-section">
      <div class="section-header-title" data-cy="forms-builder-item-title">
        <lucide-icon name="file-edit" [size]="20" class="header-icon"></lucide-icon>
        Form Item Configuration
      </div>
      <div style="flex: 1;"></div>
      <button mat-icon-button matTooltip="Help & Tutorial" (click)="openHelp()"
        style="position: relative; top: 2px;"
        data-cy="forms-builder-item-help-button">
        <lucide-icon name="circle-help" [size]="20"></lucide-icon>
      </button>
    </div>
    <!-- Content -->
    <div class="section-content" (scroll)="repositionValueAutocompletes()" data-cy="forms-builder-item-configuration-container">
      <div class="step-content">
        <!-- Basics (always visible) -->
        <div class="basics-section" data-cy="forms-builder-item-basics-section">
          <h3 class="step-title">
            <lucide-icon name="info" [size]="20"></lucide-icon>
            Basics
          </h3>
          <p class="step-description">Name, control type, and how the item is labeled to users</p>
          <div class="form-input-container" data-cy="forms-builder-item-name-container">
            <mat-form-field class="form-input" data-cy="forms-builder-item-name-field">
              <mat-label>Field ID</mat-label>
              <input matInput placeholder="Enter field ID" [(ngModel)]="item.DFormItemName"
                data-cy="forms-builder-item-name-input" />
                <mat-hint>Internal identifier only &mdash; not shown to users. See "Label" below for the text users see.</mat-hint>
              </mat-form-field>
            </div>
            <div class="form-input-container" data-cy="forms-builder-item-control-type-container">
              <mat-form-field class="form-input" data-cy="forms-builder-item-control-type-field">
                <mat-label data-cy="forms-builder-item-control-type-label">Control Type</mat-label>
                <mat-select [(ngModel)]="selectedControlTypeId"
                  (selectionChange)="onControlTypeSelectionChange($event)"
                  (opened)="focusSelectSearch(controlTypeSearch)"
                  (closed)="onControlTypeSearchInput('')"
                  panelClass="item-config-select-panel"
                  data-cy="forms-builder-item-control-type-select">
                  <div class="select-search" (click)="$event.stopPropagation()">
                    <lucide-icon name="search" [size]="16" class="select-search-icon"></lucide-icon>
                    <input #controlTypeSearch type="text" class="select-search-input"
                      placeholder="Type to filter control types" autocomplete="off"
                      [value]="controlTypeSearchQuery"
                      (input)="onControlTypeSearchInput($any($event.target).value)"
                      (keydown)="onSelectSearchKeydown($event)"
                      data-cy="forms-builder-item-control-type-search-input" />
                  </div>
                  @for (controlType of filteredControlTypes; track controlType; let i = $index) {
                    <mat-option [value]="controlType.id"
                      data-cy="forms-builder-item-control-type-option-{{i}}">
                      {{ controlType.name }}
                    </mat-option>
                  }
                  @if (filteredControlTypes.length === 0) {
                    <div class="select-empty-message" data-cy="forms-builder-item-control-type-empty">
                      No control types match your search
                    </div>
                  }
                </mat-select>
                <mat-hint>Choose the item type</mat-hint>
              </mat-form-field>
            </div>
            <div class="form-input-container" data-cy="forms-builder-item-label-container">
              <mat-form-field class="form-input" data-cy="forms-builder-item-label-field">
                <mat-label>Label</mat-label>
                <input matInput placeholder="Enter display label" [(ngModel)]="item.Label"
                  data-cy="forms-builder-item-label-input" />
                  <mat-hint>Main label shown to users</mat-hint>
                </mat-form-field>
              </div>
              <div class="form-input-container" data-cy="forms-builder-item-post-label-container">
                <mat-form-field class="form-input" data-cy="forms-builder-item-post-label-field">
                  <mat-label>Post Label</mat-label>
                  <input matInput placeholder="Enter post label" [(ngModel)]="item.PostLabel"
                    data-cy="forms-builder-item-post-label-input" />
                    <mat-hint>Optional label shown after the control</mat-hint>
                  </mat-form-field>
                </div>
                <h4 class="subsection-title">
                  <lucide-icon name="settings" [size]="20"></lucide-icon>
                  Behavior
                </h4>
                <div class="checkbox-group">
                  <div class="form-input-container" data-cy="forms-builder-item-required-container">
                    <mat-checkbox [(ngModel)]="item.isRequired" data-cy="forms-builder-item-required-checkbox">
                      Is Required
                    </mat-checkbox>
                    <p class="checkbox-hint">User must provide a value</p>
                  </div>
                  <div class="form-input-container" data-cy="forms-builder-item-read-only-patient-container">
                    <mat-checkbox [(ngModel)]="item.isReadOnlyForPatient" data-cy="forms-builder-item-read-only-patient-checkbox">
                      Is Read Only For Patient
                    </mat-checkbox>
                    <p class="checkbox-hint">Patients cannot edit this field</p>
                  </div>
                </div>
              </div>

              <!-- Values, Layout, Advanced (collapsible) -->
              <mat-accordion class="config-accordion" [multi]="true" data-cy="forms-builder-item-accordion">
                <mat-expansion-panel [(expanded)]="layoutPanelExpanded" data-cy="forms-builder-item-panel-layout">
                  <mat-expansion-panel-header>
                    <mat-panel-title>
                      <lucide-icon name="grid-3x3" [size]="20"></lucide-icon>
                      Layout
                      <button mat-icon-button matTooltip="Help: Item Sizing" class="panel-help-button"
                        (click)="$event.stopPropagation(); openHelp('item-size-configuration')"
                        data-cy="forms-builder-item-layout-help-button">
                        <lucide-icon name="circle-help" [size]="16"></lucide-icon>
                      </button>
                    </mat-panel-title>
                  </mat-expansion-panel-header>
                  <p class="step-description">Configure size and label alignment</p>
                  <div class="dimensions-section">
                    <h4 class="subsection-title">
                      <lucide-icon name="ruler" [size]="20"></lucide-icon>
                      Dimensions
                    </h4>
                    <div class="form-row">
                      <div class="form-input-container" data-cy="forms-builder-item-column-width-container">
                        <mat-form-field class="form-input" data-cy="forms-builder-item-column-width-field">
                          <mat-label>Column Width (%)</mat-label>
                          <input matInput placeholder="Width %" type="number" [(ngModel)]="item.ParagraphColumnWidth"
                            data-cy="forms-builder-item-column-width-input" />
                            <mat-hint>Percentage of the row's width (e.g. 20 = 20% of the row)</mat-hint>
                          </mat-form-field>
                        </div>
                        <div class="form-input-container" data-cy="forms-builder-item-row-height-container">
                          <mat-form-field class="form-input" data-cy="forms-builder-item-row-height-field">
                            <mat-label>Row Height</mat-label>
                            <input matInput placeholder="Height" type="number" [(ngModel)]="item.ParagraphRowHeight"
                              data-cy="forms-builder-item-row-height-input" />
                              <mat-hint>Height in rows</mat-hint>
                            </mat-form-field>
                          </div>
                        </div>
                      </div>
                      <div class="alignment-section">
                        <h4 class="subsection-title">
                          <lucide-icon name="align-justify" [size]="20"></lucide-icon>
                          Label Alignment
                        </h4>
                        <div class="form-input-container" data-cy="forms-builder-item-alignment-container">
                          <mat-form-field class="form-input" data-cy="forms-builder-item-alignment-field">
                            <mat-label data-cy="forms-builder-item-alignment-label">Alignment Type</mat-label>
                            <mat-select [(ngModel)]="selectedAlignmentType" (ngModelChange)="selectAlignmentType($event)" data-cy="forms-builder-item-alignment-select">
                              @for (type of cfgItemAlignmentTypeList; track type; let i = $index) {
                                <mat-option [value]="type.id"
                                  data-cy="forms-builder-item-alignment-option-{{i}}">
                                  <div class="cfg-form-select-button cfg-form-button-name"
                                    data-cy="forms-builder-item-alignment-button-{{i}}">
                                    {{ type.name}}
                                  </div>
                                </mat-option>
                              }
                            </mat-select>
                            <mat-hint>How label aligns with control</mat-hint>
                          </mat-form-field>
                        </div>
                      </div>
                </mat-expansion-panel>

                @if (canUseValidValues || canUseDefaultValue) {
                  <mat-expansion-panel [(expanded)]="valuesPanelExpanded" data-cy="forms-builder-item-panel-values">
                    <mat-expansion-panel-header>
                      <mat-panel-title>
                        <lucide-icon name="list" [size]="20"></lucide-icon>
                        Values &amp; Defaults
                      </mat-panel-title>
                    </mat-expansion-panel-header>
                    <p class="step-description">Configure valid options and default selections</p>
                    @if (canUseValidValues) {
                    <div class="form-input-container" data-cy="forms-builder-item-valid-values-container">
                      <mat-form-field class="form-input" data-cy="forms-builder-item-valid-values-field">
                        <mat-label data-cy="forms-builder-item-valid-values-label">Valid Values</mat-label>
                        <input matInput
                          #validValueTrigger="matAutocompleteTrigger"
                          [(ngModel)]="validValueSearchQuery"
                          [matAutocomplete]="validValueAutocomplete"
                          (input)="onValidValueSearchInput($any($event.target).value)"
                          placeholder="Type to filter valid values"
                          autocomplete="off"
                          data-cy="forms-builder-item-valid-values-search-input" />
                        @if (isFilteringValidValues) {
                          <mat-spinner matSuffix diameter="18" class="search-loading-spinner"
                            data-cy="forms-builder-item-valid-values-search-loading"></mat-spinner>
                        }
                        @if (!isFilteringValidValues && validValueSearchQuery) {
                          <button mat-icon-button matSuffix type="button" (click)="clearValidValueSearch()"
                            data-cy="forms-builder-item-valid-values-search-clear">
                            <lucide-icon name="x" [size]="20"></lucide-icon>
                          </button>
                        }
                        <mat-hint>
                          @if (selectedValidValue) {
                            Selected: {{ selectedValidValue.ValidValueName }}
                          }
                          @if (!selectedValidValue) {
                            Predefined list of valid values
                          }
                        </mat-hint>
                      </mat-form-field>
                      <!-- mat-autocomplete renders its options in a CDK overlay, so the expansion panel's
                           overflow: hidden can't clip them when this field is at the bottom of the section. -->
                      <mat-autocomplete #validValueAutocomplete="matAutocomplete" class="item-config-autocomplete-panel">
                        @if (isFilteringValidValues) {
                          <mat-option disabled data-cy="forms-builder-item-valid-values-searching">
                            Searching valid values...
                          </mat-option>
                        }
                        @for (validValue of filteredValidValues; track validValue; let i = $index) {
                          <mat-option [value]="validValue.ValidValueName"
                            [title]="getValidValuePreview(validValue)"
                            (onSelectionChange)="$event.isUserInput && onInlineValidValueSelected(validValue)"
                            data-cy="forms-builder-item-valid-value-option-{{i}}">
                            {{ validValue.ValidValueName }}
                          </mat-option>
                        }
                        @if (!isFilteringValidValues && filteredValidValues.length === 0) {
                          <mat-option disabled data-cy="forms-builder-item-valid-values-empty">
                            No valid values match your search
                          </mat-option>
                        }
                      </mat-autocomplete>
                    </div>
                  }
                  @if (canUseDefaultValue && !canSelectDefaultFromValidValue) {
                    <div class="form-input-container"
                      data-cy="forms-builder-item-default-value-container">
                      <mat-form-field class="form-input" data-cy="forms-builder-item-default-value-field">
                        <mat-label>Default Value</mat-label>
                        <input matInput placeholder="Enter default value" [(ngModel)]="item.DefaultValue"
                          data-cy="forms-builder-item-default-value-input" />
                          <mat-hint>Pre-filled value when form loads</mat-hint>
                        </mat-form-field>
                      </div>
                    }
                    @if (canUseDefaultValue && canSelectDefaultFromValidValue && selectedValidValue) {
                      <div class="form-input-container"
                        data-cy="forms-builder-item-default-select-container">
                        <mat-form-field class="form-input" data-cy="forms-builder-item-default-select-field">
                          <mat-label data-cy="forms-builder-item-default-select-label">Default Value</mat-label>
                          <input matInput
                            #defaultValueInput
                            #defaultValueTrigger="matAutocompleteTrigger"
                            [(ngModel)]="defaultValueSearchQuery"
                            [matAutocomplete]="defaultValueAutocomplete"
                            (input)="onDefaultValueSearchInput($any($event.target).value)"
                            placeholder="Type to filter selected valid values"
                            autocomplete="off"
                            data-cy="forms-builder-item-default-search-input" />
                          @if (isFilteringDefaultValues) {
                            <mat-spinner matSuffix diameter="18" class="search-loading-spinner"
                              data-cy="forms-builder-item-default-search-loading"></mat-spinner>
                          }
                          @if (!isFilteringDefaultValues && defaultValueSearchQuery) {
                            <button mat-icon-button matSuffix type="button" (click)="clearDefaultValueSearch()"
                              data-cy="forms-builder-item-default-search-clear">
                              <lucide-icon name="x" [size]="20"></lucide-icon>
                            </button>
                          }
                          <mat-hint>
                            @if (item.DefaultValue) {
                              Selected: {{ getDefaultValueDisplayText(item.DefaultValue) }}
                            }
                            @if (!item.DefaultValue) {
                              Choose from valid values list
                            }
                          </mat-hint>
                        </mat-form-field>
                        <mat-autocomplete #defaultValueAutocomplete="matAutocomplete" class="item-config-autocomplete-panel">
                          @if (isFilteringDefaultValues) {
                            <mat-option disabled data-cy="forms-builder-item-default-searching">
                              Searching default values...
                            </mat-option>
                          }
                          @for (validValueItem of filteredSelectedValidValueItems; track validValueItem; let i = $index) {
                            <mat-option [value]="validValueItem.ItemDisplayValue"
                              (onSelectionChange)="$event.isUserInput && onInlineDefaultValueSelected(validValueItem)"
                              data-cy="forms-builder-item-default-option-{{i}}">
                              {{ validValueItem.ItemDisplayValue }}
                            </mat-option>
                          }
                          @if (!isFilteringDefaultValues && filteredSelectedValidValueItems.length === 0) {
                            <mat-option disabled data-cy="forms-builder-item-default-empty">
                              No default options match your search
                            </mat-option>
                          }
                        </mat-autocomplete>
                      </div>
                    }
                  </mat-expansion-panel>
                }

                <mat-expansion-panel [(expanded)]="hierarchyPanelExpanded" data-cy="forms-builder-item-panel-hierarchy">
                  <mat-expansion-panel-header>
                    <mat-panel-title>
                      <lucide-icon name="git-branch" [size]="20"></lucide-icon>
                      Hierarchy
                    </mat-panel-title>
                  </mat-expansion-panel-header>
                  <div class="info-message">
                    <lucide-icon name="info" [size]="20"></lucide-icon>
                    <p>Parent/child relationships let items be grouped together &mdash; for example, showing or organizing a set of items under one "parent" item. Most items don't need this.</p>
                  </div>
                  <div class="form-input-container" data-cy="forms-builder-item-has-children-container">
                    <mat-checkbox [(ngModel)]="item.HasChildren" data-cy="forms-builder-item-has-children-checkbox">
                      Has Children
                    </mat-checkbox>
                    <p class="checkbox-hint">This item can act as a parent that contains other, child items</p>
                  </div>
                  <div class="form-input-container" data-cy="forms-builder-item-parent-container">
                    <mat-form-field class="form-input" data-cy="forms-builder-item-parent-field">
                      <mat-label data-cy="forms-builder-item-parent-label">Parent Item</mat-label>
                      <mat-select [(ngModel)]="selectedParentItemId"
                        (selectionChange)="onParentItemSelectionChange($event)"
                        (opened)="focusSelectSearch(parentItemSearch)"
                        (closed)="onParentItemSearchInput('')"
                        panelClass="item-config-select-panel"
                        data-cy="forms-builder-item-parent-select">
                        <div class="select-search" (click)="$event.stopPropagation()">
                          <lucide-icon name="search" [size]="16" class="select-search-icon"></lucide-icon>
                          <input #parentItemSearch type="text" class="select-search-input"
                            placeholder="Type to filter items" autocomplete="off"
                            [value]="parentItemSearchQuery"
                            (input)="onParentItemSearchInput($any($event.target).value)"
                            (keydown)="onSelectSearchKeydown($event)"
                            data-cy="forms-builder-item-parent-search-input" />
                        </div>
                        @for (otherItem of filteredOtherItemsInForm; track otherItem; let i = $index) {
                          <mat-option [value]="otherItem.DFormItemId"
                            data-cy="forms-builder-item-parent-option-{{i}}">
                            {{ otherItem.DFormItemName}}
                          </mat-option>
                        }
                        @if (filteredOtherItemsInForm.length === 0) {
                          <div class="select-empty-message" data-cy="forms-builder-item-parent-empty">
                            No items match your search
                          </div>
                        }
                        <!-- A real mat-option, not a <button>: mat-select's key manager closes the
                             panel on Tab, so nothing inside the overlay except an option can take
                             keyboard focus. The null value routes through the selectionChange
                             handler, which reads it as "clear".
                             Only rendered when something is selected — a null-valued option would
                             otherwise match an empty model and show its own label in the trigger. -->
                        @if (selectedParentItemId) {
                          <mat-option class="select-clear-option" [value]="null"
                            data-cy="forms-builder-item-parent-clear">
                            <lucide-icon name="x" [size]="16"></lucide-icon>
                            Clear parent item
                          </mat-option>
                        }
                      </mat-select>
                      <mat-hint>Makes this item a child of the selected item, instead of a top-level item</mat-hint>
                    </mat-form-field>
                  </div>
                </mat-expansion-panel>

                <mat-expansion-panel [(expanded)]="advancedPanelExpanded" data-cy="forms-builder-item-panel-advanced">
                  <mat-expansion-panel-header>
                    <mat-panel-title>
                      <lucide-icon name="sliders-horizontal" [size]="20"></lucide-icon>
                      Advanced
                    </mat-panel-title>
                  </mat-expansion-panel-header>
                  <p class="step-description">Automation and clinical note integration &mdash; most items don't need these</p>
                  <div class="actions-section">
                    <h4 class="subsection-title">
                      <lucide-icon name="zap" [size]="20"></lucide-icon>
                      Automation Actions
                    </h4>
                    <div class="form-input-container" data-cy="forms-builder-item-before-action-container">
                      <mat-form-field class="form-input" data-cy="forms-builder-item-before-action-field">
                        <mat-label data-cy="forms-builder-item-before-action-label">Before Display Action</mat-label>
                        <mat-select [(ngModel)]="selectedBeforeActionId"
                          (selectionChange)="onBeforeActionSelectionChange($event)"
                          (opened)="focusSelectSearch(beforeActionSearch)"
                          (closed)="onBeforeActionSearchInput('')"
                          panelClass="item-config-select-panel"
                          data-cy="forms-builder-item-before-action-select">
                          <div class="select-search" (click)="$event.stopPropagation()">
                            <lucide-icon name="search" [size]="16" class="select-search-icon"></lucide-icon>
                            <input #beforeActionSearch type="text" class="select-search-input"
                              placeholder="Type to filter actions" autocomplete="off"
                              [value]="beforeActionSearchQuery"
                              (input)="onBeforeActionSearchInput($any($event.target).value)"
                              (keydown)="onSelectSearchKeydown($event)"
                              data-cy="forms-builder-item-before-action-search-input" />
                          </div>
                          @for (action of filteredBeforeActions; track action; let i = $index) {
                            <mat-option [value]="action.ItemActionId"
                              data-cy="forms-builder-item-before-action-option-{{i}}">
                              {{ action.ActionName}}
                            </mat-option>
                          }
                          @if (filteredBeforeActions.length === 0) {
                            <div class="select-empty-message" data-cy="forms-builder-item-before-action-empty">
                              No actions match your search
                            </div>
                          }
                          @if (selectedBeforeActionId) {
                            <mat-option class="select-clear-option" [value]="null"
                              data-cy="forms-builder-item-before-action-clear">
                              <lucide-icon name="x" [size]="16"></lucide-icon>
                              Clear action
                            </mat-option>
                          }
                        </mat-select>
                        <mat-hint>Action triggered before item is displayed</mat-hint>
                      </mat-form-field>
                    </div>
                    @if (canUseAfterUpdateAction) {
                      <div class="form-input-container"
                        data-cy="forms-builder-item-after-action-container">
                        <mat-form-field class="form-input" data-cy="forms-builder-item-after-action-field">
                          <mat-label data-cy="forms-builder-item-after-action-label">After Update Action</mat-label>
                          <mat-select [(ngModel)]="selectedAfterActionId"
                            (selectionChange)="onAfterActionSelectionChange($event)"
                            (opened)="focusSelectSearch(afterActionSearch)"
                            (closed)="onAfterActionSearchInput('')"
                            panelClass="item-config-select-panel"
                            data-cy="forms-builder-item-after-action-select">
                            <div class="select-search" (click)="$event.stopPropagation()">
                              <lucide-icon name="search" [size]="16" class="select-search-icon"></lucide-icon>
                              <input #afterActionSearch type="text" class="select-search-input"
                                placeholder="Type to filter actions" autocomplete="off"
                                [value]="afterActionSearchQuery"
                                (input)="onAfterActionSearchInput($any($event.target).value)"
                                (keydown)="onSelectSearchKeydown($event)"
                                data-cy="forms-builder-item-after-action-search-input" />
                            </div>
                            @for (action of filteredAfterActions; track action; let i = $index) {
                              <mat-option [value]="action.ItemActionId"
                                data-cy="forms-builder-item-after-action-option-{{i}}">
                                {{ action.ActionName}}
                              </mat-option>
                            }
                            @if (filteredAfterActions.length === 0) {
                              <div class="select-empty-message" data-cy="forms-builder-item-after-action-empty">
                                No actions match your search
                              </div>
                            }
                            @if (selectedAfterActionId) {
                              <mat-option class="select-clear-option" [value]="null"
                                data-cy="forms-builder-item-after-action-clear">
                                <lucide-icon name="x" [size]="16"></lucide-icon>
                                Clear action
                              </mat-option>
                            }
                          </mat-select>
                          <mat-hint>Action triggered after value is updated</mat-hint>
                        </mat-form-field>
                      </div>
                    }
                    @if (!canUseAfterUpdateAction) {
                      <div class="info-message">
                        <lucide-icon name="info" [size]="20"></lucide-icon>
                        <p>After update action not available for this control type</p>
                      </div>
                    }
                  </div>
                  <div class="note-builder-section">
                    <h4 class="subsection-title">
                      <lucide-icon name="file-plus" [size]="20"></lucide-icon>
                      Note Builder
                    </h4>
                    <div class="form-input-container" data-cy="forms-builder-item-note-enabled-container">
                      <mat-checkbox [(ngModel)]="item.isNoteEnabled" data-cy="forms-builder-item-note-enabled-checkbox">
                        Use for Note Builder
                      </mat-checkbox>
                      <p class="checkbox-hint">Include this item in note generation</p>
                    </div>
                    @if (item.isNoteEnabled) {
                      <div class="note-builder-config">
                        <div class="form-input-container" data-cy="forms-builder-item-include-value-in-note-container">
                          <mat-checkbox [(ngModel)]="item.IncludeValueInNote" data-cy="forms-builder-item-include-value-in-note-checkbox">
                            Include Value in Note
                          </mat-checkbox>
                          <p class="checkbox-hint">Show the item's value in generated notes</p>
                        </div>
                        <div class="form-input-container" data-cy="forms-builder-item-value-trigger-container">
                          <mat-form-field class="form-input" data-cy="forms-builder-item-value-trigger-field">
                            <mat-label data-cy="forms-builder-item-value-trigger-label">Value Trigger</mat-label>
                            <mat-select [(ngModel)]="selectedValueTriggerId"
                              (selectionChange)="onValueTriggerSelectionChange($event)"
                              (opened)="focusSelectSearch(valueTriggerSearch)"
                              (closed)="onValueTriggerSearchInput('')"
                              panelClass="item-config-select-panel"
                              data-cy="forms-builder-item-value-trigger-select">
                              <div class="select-search" (click)="$event.stopPropagation()">
                                <lucide-icon name="search" [size]="16" class="select-search-icon"></lucide-icon>
                                <input #valueTriggerSearch type="text" class="select-search-input"
                                  placeholder="Type to filter values" autocomplete="off"
                                  [value]="valueTriggerSearchQuery"
                                  (input)="onValueTriggerSearchInput($any($event.target).value)"
                                  (keydown)="onSelectSearchKeydown($event)"
                                  data-cy="forms-builder-item-value-trigger-search-input" />
                              </div>
                              @for (valueTrigger of filteredValueTriggers; track valueTrigger; let i = $index) {
                                <mat-option [value]="valueTrigger.ValidValueItemId"
                                  data-cy="forms-builder-item-value-trigger-option-{{i}}">
                                  {{ valueTrigger.ItemDisplayValue }}
                                </mat-option>
                              }
                              @if (filteredValueTriggers.length === 0) {
                                <div class="select-empty-message" data-cy="forms-builder-item-value-trigger-empty">
                                  No values match your search
                                </div>
                              }
                              @if (selectedValueTriggerId) {
                                <mat-option class="select-clear-option" [value]="null"
                                  data-cy="forms-builder-item-value-trigger-clear">
                                  <lucide-icon name="x" [size]="16"></lucide-icon>
                                  Clear value trigger
                                </mat-option>
                              }
                            </mat-select>
                            <mat-hint>Value that triggers note inclusion &mdash; comes from the Valid Value chosen above</mat-hint>
                          </mat-form-field>
                        </div>
                        <div class="form-input-container" data-cy="forms-builder-item-note-container">
                          <mat-form-field class="form-input" data-cy="forms-builder-item-note-field">
                            <mat-label>Note Text</mat-label>
                            <input matInput placeholder="Enter note text" [(ngModel)]="item.Note"
                              data-cy="forms-builder-item-note-input" />
                              <mat-hint>Text to include in generated note</mat-hint>
                            </mat-form-field>
                          </div>
                        </div>
                      }
                    </div>
                  </mat-expansion-panel>
                </mat-accordion>
              </div>
            </div>
            <!-- Bottom Action Bar (Fixed) -->
                  <div class="action-container" data-cy="forms-builder-item-action-container">
                    <div class="primary-actions">
                      <button mat-raised-button color="primary" class="action-button" (click)="save()"
                        data-cy="forms-builder-item-save-button">
                        <lucide-icon name="check" [size]="20"></lucide-icon>
                        Save
                      </button>
                      <button mat-raised-button color="warn" class="action-button" (click)="cancel()"
                        data-cy="forms-builder-item-cancel-button">
                        <lucide-icon name="x" [size]="20"></lucide-icon>
                        Cancel
                      </button>
                    </div>
                    <div class="secondary-actions">
                      <button mat-raised-button color="primary" class="action-button" (click)="duplicate()"
                        data-cy="forms-builder-item-duplicate-button">
                        <lucide-icon name="copy" [size]="20"></lucide-icon>
                        Duplicate
                      </button>
                      <button mat-button color="warn" class="action-button-delete" matTooltip="Delete item from the form"
                        (click)="delete()" data-cy="forms-builder-item-delete-button">
                        <lucide-icon name="trash-2" [size]="20"></lucide-icon>
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              }

              @if (isLoading) {
                <div class="spinner-container" data-cy="forms-builder-item-loading">
                  <mat-spinner data-cy="forms-builder-item-spinner"></mat-spinner>
                </div>
              }
